// content/bosses/boss4.ts — BOSS 4: MOTHER OF LAMPS (DESIGN §5, Zone 4).
//
//   "Verb: fight the light economy — the boss is only real where there is light."
//
// A moth-matriarch the size of the chamber's ceiling, in a round room with four
// braziers. She has no health-bar trick and no add-priority puzzle. She has one
// rule, and the whole fight is that rule:
//
//   SHE IS ONLY REAL WHERE THERE IS LIGHT.
//
//   · inside a lit BRAZIER's radius -> visible, and every hit lands
//   · inside the Warden's LAMP only -> visible, ghostly, and hits ping off
//   · outside both                  -> invisible, and hits ping off
//
// Her attacks come out of the dark regardless, and every one of them is
// telegraphed by a glint-trail lane that the zone's darkness mask is not allowed
// to eat (content/zones/zone4.ts renders arenaFx above it). So the dark is never
// a cheap shot — it is only ever a DPS problem.
//
// The loop the fight wants you to find: light a brazier -> she comes to eat it
// -> while she eats it she is standing in it -> burn her -> she wins that
// exchange -> light another. P1 teaches it at one brazier per 10s. P2 takes two
// at a time, shrinks your lamp with a dust storm and sends Lampeaters to fight
// your relights. P3 blows all four out in one wingbeat every 15s and chains its
// rushes three deep, so the free windows stop arriving and you have to make them.
//
// Every literal comes from data/zones/zone4.ts (ZONE4_BOSS). The numbers here
// are drawing geometry.

import type { Draw, RunCtx, Vec } from '../../types'
import { Boss, registerBoss } from '../../game/boss'
import { bossDefeated, registerBossArena } from '../../game/zones'
import { ZONE4_BOSS, ZONE4_BOSS_ARENA, ZONE4_COLORS } from '../../data/zones/zone4'
import { arenaFx, douseBrazier, relightAllBraziers, zone4Lights } from '../zones/zone4'
import { emit } from '../../engine/particles'
import { PixelArt, drawSprite, registerPainter, registerRecipe, shade } from '../../engine/sprites'

const D = ZONE4_BOSS
const C = ZONE4_COLORS

/** Per-phase array read, clamped — phases are 1-based. */
function byPhase<T>(arr: readonly T[], phase: number): T {
  return arr[Math.max(0, Math.min(arr.length - 1, phase - 1))]
}

const scratch: Vec = { x: 0, y: 0 }

type DousePhase = 'idle' | 'approach' | 'flutter'

class MotherOfLamps extends Boss {
  readonly id = D.id
  /** read structurally by game/hud.ts for the boss bar */
  readonly name = D.name
  readonly phaseTicks = [D.phase3At, D.phase2At]

  private built = false
  private anim = 0

  // --- the douse cycle
  private dousePhase: DousePhase = 'idle'
  private douseT = 0
  private douseWind = 0
  private douseTargets: number[] = []

  // --- the wing-rush
  private rushT = 0
  private rushWind = 0
  private rushGap = 0
  private rushLeft = 0
  private rushing = false
  private rushDx = 0
  private rushDy = 0
  private rushLen = 0
  private rushGone = 0
  private rushHit = false

  // --- escorts
  private mothT = 0
  private eaterT = 0

  // --- drift
  private drift = 0

  private dying = false
  private tookDamage = false
  private lastHp = -1
  private hurtT = 0
  private sealBarked = false

  constructor() {
    super(D.hp)
    this.radius = D.radius
    this.pos.x = D.centre.x
    this.pos.y = D.centre.y - D.driftRadius * 0.5
  }

  // ------------------------------------------------------------------ build

  private build(ctx: RunCtx): void {
    this.built = true
    arenaFx.active = true
    arenaFx.gust = 0
    arenaFx.dustR = 0
    arenaFx.seal.t = 0
    for (const l of arenaFx.lanes) l.on = false
    this.dousePhase = 'idle'
    this.douseT = byPhase(D.douse.interval, 1) * 0.55
    this.rushT = byPhase(D.rush.interval, 1) * 0.7
    this.mothT = byPhase(D.spawn.moths.interval, 1) * 0.6
    this.eaterT = 0
    this.lastHp = ctx.player.hp
    ctx.bark('THE MOTHER OF LAMPS. She is only real where the fire is.')
    ctx.sfx('door')
    ctx.shake(9, 1)
  }

  // ----------------------------------------------------------------- phases

  protected onPhase(phase: number): void {
    const ctx = this.ctx
    if (!ctx) return
    if (phase === 2) {
      // the dust storm: your own lamp is 30% smaller for the rest of the fight
      zone4Lights.lampMul = D.dust.lampMul
      this.eaterT = byPhase(D.spawn.lampeaters.interval, 2) * 0.5
      ctx.bark('Dust. Your lamp shrinks, and she takes two at a time.')
    } else if (phase === 3) {
      this.rushT = Math.min(this.rushT, byPhase(D.rush.interval, 3))
      this.douseT = Math.min(this.douseT, 4)
      ctx.bark('One wingbeat now, and every fire in the room goes out. Chain your relights.')
    }
    ctx.shake(7, 0.7)
    ctx.sfx('telegraph')
  }

  private applyPhase(): void {
    const f = this.healthFraction
    this.setPhase(f <= D.phase3At ? 3 : f <= D.phase2At ? 2 : 1)
  }

  /**
   * THE RULE. Braziers make her real; the Warden's lamp does not. A hit that
   * lands while she is unlit costs nothing and says so — a cold ring, a ping and
   * a line of text, so a player never concludes their weapon is broken.
   */
  private get vulnerable(): boolean {
    return zone4Lights.inBrazierLight(this.pos.x, this.pos.y)
  }

  takeDamage(n: number): void {
    if (!this.alive || n <= 0) return
    if (!this.vulnerable) {
      arenaFx.seal.t = 0.5
      arenaFx.seal.x = this.pos.x
      arenaFx.seal.y = this.pos.y
      this.ctx?.sfx('ui')
      if (!this.sealBarked) {
        this.sealBarked = true
        this.ctx?.bark('Nothing. She is not there — not until a brazier says she is.')
      }
      return
    }
    const before = this.hp
    super.takeDamage(n)
    if (this.hp < before) this.hurtT = 0.12
    this.applyPhase()
  }

  // ----------------------------------------------------------------- update

  update(ctx: RunCtx, dt: number): void {
    this.tick(ctx, dt)
    if (!this.built) this.build(ctx)
    this.anim += dt
    if (this.hurtT > 0) this.hurtT -= dt
    if (arenaFx.seal.t > 0) arenaFx.seal.t -= dt
    if (arenaFx.gust > 0) arenaFx.gust = Math.max(0, arenaFx.gust - dt * 1.2)

    if (this.dying) return
    if (!this.alive) {
      this.die(ctx)
      return
    }

    // no-hit watch, same reading as bosses 1-3: any drop in the Warden's health
    // during the fight spends the clean kill. Under ?invuln=1 nothing reaches the
    // player, so an invulnerable run reads as untouched — true, and noted where
    // the test asserts it.
    if (this.lastHp >= 0 && ctx.player.hp < this.lastHp) this.tookDamage = true
    this.lastHp = ctx.player.hp

    this.applyPhase()
    const phase = this.phase

    // P2+ : the dust storm rides the zone's own lamp radius and draws a ring
    arenaFx.dustR = phase >= 2 ? zone4Lights.lampRadius : 0
    arenaFx.ghost.on = true
    arenaFx.ghost.x = this.pos.x
    arenaFx.ghost.y = this.pos.y

    this.updateRush(ctx, dt, phase)
    if (!this.rushing) this.updateDouse(ctx, dt, phase)
    this.updateSpawns(ctx, dt, phase)
    this.clampToChamber()
  }

  private clampToChamber(): void {
    const dx = this.pos.x - D.centre.x
    const dy = (this.pos.y - D.centre.y) / 0.56
    const d = Math.hypot(dx, dy)
    const max = 15.5 * 32
    if (d > max) {
      this.pos.x = D.centre.x + (dx / d) * max
      this.pos.y = D.centre.y + ((dy / d) * max) * 0.56
    }
  }

  // ------------------------------------------------------------- the douse
  //
  // P1/P2 fly to the brazier and hover over it for the flutter — which is
  // exactly the window in which she is standing in light and can be hurt. P3
  // does it from wherever she is, which is why P3 is the pressure phase.

  private updateDouse(ctx: RunCtx, dt: number, phase: number): void {
    if (this.dousePhase === 'idle') {
      // She is a moth. Between douses she circles whatever is burning rather
      // than the middle of the room — which is what keeps the fight's loop alive
      // in P3, where the wingbeat is cast from range and she would otherwise
      // never enter light at all between all-douses. Light a brazier and she
      // drifts to it; that is the invitation, and the DPS window.
      this.drift += D.driftSpeed * dt
      const lit = zone4Lights.nearestLit(D.centre.x, D.centre.y)
      const b = lit >= 0 ? zone4Lights.braziers[lit] : null
      const ax = b ? b.x : D.centre.x
      const ay = b ? b.y : D.centre.y
      const r = b ? D.hoverRadius : D.driftRadius
      const tx = ax + Math.cos(this.drift) * r
      const ty = ay + Math.sin(this.drift) * r * 0.5
      this.glideTo(tx, ty, D.approachSpeed * (b ? 0.8 : 0.45), dt)
      this.douseT -= dt
      if (this.douseT <= 0) this.beginDouse(ctx, phase)
      return
    }

    if (this.dousePhase === 'approach') {
      const i = this.douseTargets[0]
      const b = zone4Lights.braziers[i]
      if (!b || !b.lit) {
        this.endDouse(phase)
        return
      }
      this.glideTo(b.x, b.y, D.approachSpeed, dt)
      if (Math.hypot(b.x - this.pos.x, b.y - this.pos.y) < 46) {
        this.dousePhase = 'flutter'
        this.douseWind = byPhase(D.douse.windup, phase)
        this.telegraph('douse', this.douseWind)
        ctx.bark('She settles on the fire. That is the window.')
      }
      return
    }

    // flutter: wings up, telegraphed, and if she is over a brazier she is REAL
    this.douseWind -= dt
    if (ctx.rng() < 0.6) {
      emit(this.pos.x, this.pos.y - 10, {
        count: 2,
        color: '#8e7f6a',
        speed: 120,
        speedVar: 80,
        life: 0.5,
        size: 3,
        shape: 1,
      })
    }
    if (this.douseWind > 0) return

    for (const i of this.douseTargets) douseBrazier(i, true)
    if (this.douseTargets.length >= 3) {
      arenaFx.gust = 1
      ctx.shake(12, 0.7)
      ctx.hitstop(0.06)
      ctx.sfx('generator-die')
      ctx.bark('Every lamp at once. Move, and light them again.')
    } else {
      ctx.shake(4, 0.35)
      ctx.sfx('door')
    }
    this.endDouse(phase)
  }

  private beginDouse(ctx: RunCtx, phase: number): void {
    this.douseTargets.length = 0
    const want = byPhase(D.douse.count, phase)
    const list = zone4Lights.braziers
    if (want < 0) {
      for (let i = 0; i < list.length; i++) if (list[i].lit) this.douseTargets.push(i)
    } else {
      // the ones nearest her, so the approach is short and the window is real
      const order: number[] = []
      for (let i = 0; i < list.length; i++) if (list[i].lit) order.push(i)
      order.sort(
        (a, b) =>
          Math.hypot(list[a].x - this.pos.x, list[a].y - this.pos.y) -
          Math.hypot(list[b].x - this.pos.x, list[b].y - this.pos.y),
      )
      for (let i = 0; i < Math.min(want, order.length); i++) this.douseTargets.push(order[i])
    }
    if (this.douseTargets.length === 0) {
      // nothing burning: she waits, and the room stays hers
      this.douseT = 1.5
      return
    }
    if (byPhase(D.douse.approach, phase) && this.douseTargets.length === 1) {
      this.dousePhase = 'approach'
      return
    }
    if (byPhase(D.douse.approach, phase)) {
      // two at once: she goes to the first and the wingbeat reaches both
      this.dousePhase = 'approach'
      return
    }
    this.dousePhase = 'flutter'
    this.douseWind = byPhase(D.douse.windup, phase)
    this.telegraph('wingbeat', this.douseWind)
    ctx.sfx('telegraph')
    ctx.shake(3, 0.5)
  }

  private endDouse(phase: number): void {
    this.dousePhase = 'idle'
    this.douseTargets.length = 0
    this.douseT = byPhase(D.douse.interval, phase)
  }

  private glideTo(tx: number, ty: number, speed: number, dt: number): void {
    const dx = tx - this.pos.x
    const dy = ty - this.pos.y
    const d = Math.hypot(dx, dy)
    if (d < 2) return
    const step = Math.min(d, speed * dt)
    this.pos.x += (dx / d) * step
    this.pos.y += (dy / d) * step
  }

  // -------------------------------------------------------------- the rush
  //
  // A linear charge across the chamber. The telegraph is the lane itself, drawn
  // by the zone above the darkness mask: you always know where the charge will
  // pass, even when you have no idea where she is.

  private updateRush(ctx: RunCtx, dt: number, phase: number): void {
    const lane = arenaFx.lanes[0]

    if (this.rushing) {
      const speed = byPhase(D.rush.speed, phase)
      const step = speed * dt
      this.pos.x += this.rushDx * step
      this.pos.y += this.rushDy * step
      this.rushGone += step
      if (!this.rushHit) {
        const d = Math.hypot(ctx.player.pos.x - this.pos.x, ctx.player.pos.y - this.pos.y)
        // PlayerState (frozen) carries no radius; the run's Player does. Same
        // structural read boss 2 uses for i-frames.
        const pr = (ctx.player as unknown as { radius?: number }).radius ?? 10
        if (d < this.radius * 0.8 + pr) {
          this.rushHit = true
          ctx.damagePlayer(D.rush.damage, 'wing-rush')
          ctx.pushPlayer(this.rushDx * 260, this.rushDy * 260)
          ctx.shake(6, 0.3)
        }
      }
      if (ctx.rng() < 0.9) {
        emit(this.pos.x, this.pos.y, {
          count: 2,
          color: '#e8dcbf',
          speed: 90,
          life: 0.35,
          size: 3,
          shape: 1,
        })
      }
      if (this.rushGone >= this.rushLen) {
        this.rushing = false
        lane.on = false
        this.rushLeft--
        if (this.rushLeft > 0) this.rushGap = D.rush.chainGap
        else this.rushT = byPhase(D.rush.interval, phase)
      }
      return
    }

    if (this.rushGap > 0) {
      this.rushGap -= dt
      if (this.rushGap <= 0) this.armRush(ctx, phase)
      return
    }

    if (this.rushWind > 0) {
      this.rushWind -= dt
      const total = byPhase(D.rush.windup, phase)
      lane.k = 1 - Math.max(0, this.rushWind) / total
      if (this.rushWind <= 0) {
        this.rushing = true
        this.rushGone = 0
        this.rushHit = false
        lane.k = -1
        ctx.sfx('dash')
        ctx.shake(5, 0.25)
      }
      return
    }

    this.rushT -= dt
    if (this.rushT > 0) return
    this.rushLeft = byPhase(D.rush.chain, phase)
    this.armRush(ctx, phase)
  }

  private armRush(ctx: RunCtx, phase: number): void {
    const dx = ctx.player.pos.x - this.pos.x
    const dy = ctx.player.pos.y - this.pos.y
    const l = Math.hypot(dx, dy) || 1
    this.rushDx = dx / l
    this.rushDy = dy / l
    this.rushLen = Math.max(320, l + 240)
    this.rushWind = byPhase(D.rush.windup, phase)
    const lane = arenaFx.lanes[0]
    lane.on = true
    lane.k = 0
    lane.x0 = this.pos.x
    lane.y0 = this.pos.y
    lane.x1 = this.pos.x + this.rushDx * this.rushLen
    lane.y1 = this.pos.y + this.rushDy * this.rushLen
    this.telegraph('wing-rush', this.rushWind)
  }

  // ------------------------------------------------------------- the escorts

  private liveChildren(ctx: RunCtx): number {
    let n = 0
    const list = ctx.enemies
    for (let i = 0; i < list.length; i++) if (list[i].alive && list[i].tags.z4mother !== undefined) n++
    return n
  }

  private updateSpawns(ctx: RunCtx, dt: number, phase: number): void {
    this.mothT -= dt
    if (this.mothT <= 0) {
      this.mothT = byPhase(D.spawn.moths.interval, phase)
      this.scatter(ctx, D.spawn.moths.body, byPhase(D.spawn.moths.count, phase))
    }
    const ei = byPhase(D.spawn.lampeaters.interval, phase)
    if (ei > 0) {
      this.eaterT -= dt
      if (this.eaterT <= 0) {
        this.eaterT = ei
        this.scatter(ctx, D.spawn.lampeaters.body, byPhase(D.spawn.lampeaters.count, phase))
        ctx.bark('Lampeaters. They will take what you light.')
      }
    }
  }

  private scatter(ctx: RunCtx, body: string, count: number): void {
    for (let k = 0; k < count; k++) {
      if (this.liveChildren(ctx) >= D.spawn.liveCap) return
      const a = ctx.rng() * Math.PI * 2
      scratch.x = this.pos.x + Math.cos(a) * 60
      scratch.y = this.pos.y + Math.sin(a) * 40
      const e = ctx.spawnEnemy(body, scratch, { elite: null })
      if (!e.alive) continue
      e.tags.z4mother = 1
      emit(scratch.x, scratch.y, { count: 8, color: C.moth, speed: 150, life: 0.4, size: 3, shape: 1 })
    }
    ctx.sfx('shoot')
  }

  // ------------------------------------------------------------------ death

  private die(ctx: RunCtx): void {
    this.dying = true
    arenaFx.active = false
    arenaFx.gust = 0
    arenaFx.dustR = 0
    for (const l of arenaFx.lanes) l.on = false
    zone4Lights.lampMul = 1

    ctx.hitstop(D.death.hitstop)
    ctx.shake(D.death.shake, D.death.shakeSeconds)
    ctx.sfx('generator-die')
    ctx.sfx('death')
    for (let i = 0; i < 3; i++) {
      emit(this.pos.x, this.pos.y, {
        count: 44,
        color: i === 0 ? '#fff0c4' : i === 1 ? C.moth : '#3b2f4a',
        speed: 260 + i * 110,
        speedVar: 220,
        life: 1,
        lifeVar: 0.5,
        size: 5,
        drag: 0.88,
      })
    }
    const list = ctx.enemies
    for (let i = list.length - 1; i >= 0; i--) if (list[i].alive) list[i].kill(true)

    // her death relights the chamber: the room exhales (DESIGN §5 Boss 4)
    relightAllBraziers()

    ctx.featTick('boss4_kill')
    if (!this.tookDamage) {
      ctx.featTick('boss4_nohit')
      ctx.bark('Steady light. She never touched you.')
    }
    ctx.bark('The Mother of Lamps goes out. Every fire in the room comes up.')
    bossDefeated(ctx)
  }

  // ----------------------------------------------------------------- render
  //
  // Drawn UNDER the zone's darkness mask, on purpose: "invisible in the dark"
  // then costs this file nothing at all — the mask simply does not have a hole
  // where she is. The only case that needs a decision is the lamp: there IS a
  // hole there, so she is dimmed by hand to the ghost the rule asks for.

  render(d: Draw): void {
    const ctx = d.ctx
    const lit = this.vulnerable
    const ghost = !lit && zone4Lights.inLampLight(this.pos.x, this.pos.y)
    const t = this.anim

    if (lit) {
      // the pool of her own shadow, and the rim that says "she is real"
      ctx.save()
      ctx.globalAlpha = 0.4
      ctx.fillStyle = '#000000'
      ctx.beginPath()
      ctx.ellipse(this.pos.x, this.pos.y + 40, 58, 15, 0, 0, Math.PI * 2)
      ctx.fill()
      // "she is real, hit her NOW" — the single most important read in the
      // fight, so it is a hot double rim rather than a hint
      ctx.globalAlpha = 0.5 + 0.25 * Math.sin(t * 7)
      ctx.strokeStyle = C.lampHot
      ctx.lineWidth = 3
      ctx.beginPath()
      ctx.arc(this.pos.x, this.pos.y, this.radius + 15, 0, Math.PI * 2)
      ctx.stroke()
      ctx.globalAlpha = 0.22 + 0.14 * Math.sin(t * 7)
      ctx.strokeStyle = C.ember
      ctx.lineWidth = 9
      ctx.beginPath()
      ctx.arc(this.pos.x, this.pos.y, this.radius + 20, 0, Math.PI * 2)
      ctx.stroke()
      ctx.restore()
    }

    const beat = this.dousePhase === 'flutter' || this.rushWind > 0 ? 1 + 0.09 * Math.sin(t * 26) : 1 + 0.03 * Math.sin(t * 3)
    drawSprite(ctx, 'motheroflamps', this.pos.x, this.pos.y + Math.sin(t * 1.4) * 3, {
      scale: 2.15 * beat,
      alpha: ghost ? 0.34 : 1,
      white: this.hurtT > 0 ? 0.85 : 0,
    })

    if (ghost) {
      // a cold outline: you can see her, you simply cannot touch her
      ctx.save()
      ctx.globalAlpha = 0.34 + 0.12 * Math.sin(t * 4)
      ctx.strokeStyle = '#6d7cab'
      ctx.lineWidth = 2
      ctx.setLineDash([9, 7])
      ctx.beginPath()
      ctx.arc(this.pos.x, this.pos.y, this.radius + 10, 0, Math.PI * 2)
      ctx.stroke()
      ctx.setLineDash([])
      ctx.restore()
    }

    if (lit || ghost) this.renderTelegraphs(d)
  }
}

// ---------------------------------------------------------------- sprites
// A great moth-matriarch. Wings first and wings biggest — at 2.15x she spans a
// third of the chamber — with a crown of dead miners' lamps and the eye-spots
// that make a moth read as a face when it should not.

registerPainter('motheroflamps', (a: PixelArt, _p, _pal) => {
  const s = a.size
  const cx = s / 2
  const wing = '#b79ad6'
  const wingLo = shade(wing, -0.5)
  const wingHi = shade(wing, 0.3)
  const fur = '#4a3a52'
  const furLo = shade(fur, -0.4)
  const lamp = '#ffc46b'
  const eye = '#ffe3a0'

  // the great upper wings, swept back and ragged at the trailing edge
  a.ellipse(cx - s * 0.3, s * 0.36, s * 0.26, s * 0.3, wingLo)
  a.ellipse(cx + s * 0.3, s * 0.36, s * 0.26, s * 0.3, wingLo)
  a.ellipse(cx - s * 0.29, s * 0.34, s * 0.21, s * 0.25, wing)
  a.ellipse(cx + s * 0.29, s * 0.34, s * 0.21, s * 0.25, wing)
  a.ellipse(cx - s * 0.3, s * 0.28, s * 0.11, s * 0.12, wingHi)
  a.ellipse(cx + s * 0.3, s * 0.28, s * 0.11, s * 0.12, wingHi)
  // the eye-spots — the face that is not a face
  a.disc(cx - s * 0.31, s * 0.34, s * 0.09, furLo)
  a.disc(cx + s * 0.31, s * 0.34, s * 0.09, furLo)
  a.disc(cx - s * 0.31, s * 0.34, s * 0.055, lamp)
  a.disc(cx + s * 0.31, s * 0.34, s * 0.055, lamp)
  a.set(Math.round(cx - s * 0.31), Math.round(s * 0.33), '#fffbe8')
  a.set(Math.round(cx + s * 0.31), Math.round(s * 0.33), '#fffbe8')
  // wing veins
  for (let i = 0; i < 4; i++) {
    const y = Math.round(s * (0.2 + i * 0.09))
    a.hline(Math.round(cx - s * 0.48), y, Math.round(s * 0.2), wingLo)
    a.hline(Math.round(cx + s * 0.28), y, Math.round(s * 0.2), wingLo)
  }
  // the lower wings, smaller and heavier
  a.ellipse(cx - s * 0.24, s * 0.66, s * 0.17, s * 0.15, wingLo)
  a.ellipse(cx + s * 0.24, s * 0.66, s * 0.17, s * 0.15, wingLo)
  a.ellipse(cx - s * 0.23, s * 0.65, s * 0.12, s * 0.1, wing)
  a.ellipse(cx + s * 0.23, s * 0.65, s * 0.12, s * 0.1, wing)

  // the thorax: heavy, furred, unmistakably a body
  a.ellipse(cx, s * 0.52, s * 0.14, s * 0.28, furLo)
  a.ellipse(cx, s * 0.5, s * 0.1, s * 0.24, fur)
  for (let i = 0; i < 5; i++) a.hline(Math.round(cx - s * 0.1), Math.round(s * (0.42 + i * 0.08)), Math.round(s * 0.2), furLo)
  a.ellipse(cx, s * 0.78, s * 0.07, s * 0.1, furLo)

  // the head, and two lamp-eyes that are the only warm thing on her
  a.ellipse(cx, s * 0.22, s * 0.11, s * 0.1, fur)
  a.disc(cx - s * 0.05, s * 0.21, s * 0.035, '#1a1220')
  a.disc(cx + s * 0.05, s * 0.21, s * 0.035, '#1a1220')
  a.set(Math.round(cx - s * 0.05), Math.round(s * 0.21), eye)
  a.set(Math.round(cx + s * 0.05), Math.round(s * 0.21), eye)

  // feathered antennae, wide as a crown
  for (let i = 1; i <= 6; i++) {
    const k = i / 6
    a.set(Math.round(cx - s * 0.06 - k * s * 0.2), Math.round(s * 0.16 - k * s * 0.1), wing)
    a.set(Math.round(cx + s * 0.05 + k * s * 0.2), Math.round(s * 0.16 - k * s * 0.1), wing)
    if (i % 2 === 0) {
      a.set(Math.round(cx - s * 0.06 - k * s * 0.2), Math.round(s * 0.16 - k * s * 0.1) + 2, wingLo)
      a.set(Math.round(cx + s * 0.05 + k * s * 0.2), Math.round(s * 0.16 - k * s * 0.1) + 2, wingLo)
    }
  }

  // the crown of dead lamps she has taken, hung off her shoulders
  for (let i = -2; i <= 2; i++) {
    if (i === 0) continue
    const x = Math.round(cx + i * s * 0.09)
    const y = Math.round(s * 0.1 + Math.abs(i) * 3)
    a.vline(x, y, 5, '#2a2b35')
    a.ellipse(x, y + 7, 3, 4, '#2f3341')
    a.set(x, y + 7, i % 2 === 0 ? '#4a3a34' : lamp)
  }
})

registerRecipe('motheroflamps', { key: 'motheroflamps', size: 64 })

// ---------------------------------------------------------------- registry

registerBossArena(D.id, ZONE4_BOSS_ARENA)
registerBoss(D.id, () => new MotherOfLamps())
